import * as Blockly from 'blockly/core'
import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import BlocklyComponent from '../components/blockly/blockly'
import { DevTools } from '../components/devTools/devTools'
import { ErrorBoundary } from '../components/errorBoundary'
import HeaderMenu from '../components/headerMenu'
import { PreviewModal } from '../components/preview/PreviewModal'
import { PreviewControlProvider } from '../components/preview/previewControlContext'
import { getPreviewDisplayMode } from '../components/preview/previewDisplayMode'
import { usePreviewModal } from '../components/preview/usePreviewModal'
import { usePreviewVisible } from '../components/preview/usePreviewVisible'
import { MissionResponseData } from '../openapi'
import { ProjectProvider, ProjectContext } from '../components/project/provider'
import { WindowVideo } from '../components/video/floating/windowVideo'
import '../styles/global.css'
import { Tutorial } from '../components/tutorials/tutorial'
import { DownloadTutorial } from '../components/tutorials/downloadTutorial'
import { TutorialProvider } from '../components/tutorials/tutorialContext'
import { useTutorialContext } from '../components/tutorials/useTutorialContext'
import { useTutorialAutoPlay } from '../components/tutorials/useTutorialAutoPlay'
import { UserContext } from '@/components/user/provider'
import { LinkageProvider } from '@/components/linkage/linkageProvider'
import { useLinkage } from '@/components/linkage/useLinkage'
import { isSupportedDevice } from '@/utils/deviceDetector'
import { isTabletSupportedMission } from '@/utils/tabletSupportedMission'

/**
 * PreviewModal の workspaces が未取得時のフォールバック。
 * 毎レンダで新 Map を作ると PreviewPanel が無駄に再マウントされるため
 * モジュール定数として安定参照にする。
 */
const EMPTY_WORKSPACES: Map<string, Blockly.Workspace> = new Map()

/**
 * PreviewModal と、その scene/workspaces を供給する usePreviewModal を内包する。
 * プレビュー非表示時（siteName 未取得、または表示形態が hidden）は
 * この host ごとアンマウントし、scene JSON fetch や workspace 購読といった副作用も
 * 動かさないようにする。
 */
function PreviewModalHost({
  missionInfo,
}: {
  missionInfo: MissionResponseData | undefined
}) {
  // scene と workspaces はミッションの実 objects から動的決定される（hook内）。
  const preview = usePreviewModal(missionInfo)

  return (
    <PreviewModal
      scene={preview.scene}
      workspaces={preview.workspaces ?? EMPTY_WORKSPACES}
      refreshKey={preview.refreshKey}
    />
  )
}

// Inner component that consumes ProjectContext and passes mission info to TutorialProvider
function EditorContent() {
  const { missionInfo, loading, isCopy, isTutorialMission } =
    useContext(ProjectContext)
  const navigate = useNavigate()

  // 非 PC（タブレット）のミッションガード。App の DeviceCheck はタブレットの
  // エディタ表示を保留しており、ミッション情報が取得できた時点でここが判定する。
  // タブレットで利用できるのはチュートリアル対象ミッション（RJF01 等）のみで、
  // それ以外は従来どおり非対応画面へ送る。リミックス（コピー）したチュートリアル
  // ミッションも利用可能にするため、コピーを除外しない isTabletSupportedMission で判定する。
  useEffect(() => {
    if (isSupportedDevice()) {
      return
    }
    if (missionInfo !== undefined && !isTabletSupportedMission(missionInfo)) {
      navigate('/unsupported')
    }
  }, [missionInfo, navigate])

  return (
    <TutorialProvider
      missionInfo={missionInfo}
      loading={loading}
      isCopy={isCopy}
      isTutorialMission={isTutorialMission}
    >
      <EditorWithTutorial isCopy={isCopy} />
    </TutorialProvider>
  )
}

// Inner component that uses TutorialContext
function EditorWithTutorial({ isCopy }: { isCopy: boolean }) {
  const { isOnlineUser } = useContext(UserContext)
  const { missionInfo } = useContext(ProjectContext)
  const {
    startInitialTutorial,
    startDownloadTutorial,
    setStartDownloadTutorial,
  } = useTutorialContext()
  const { autoPlay, handleTutorialEnd } =
    useTutorialAutoPlay(startInitialTutorial)
  const { connected } = useLinkage()

  useEffect(() => {
    if (connected && startDownloadTutorial) {
      setStartDownloadTutorial(false)
    }
  }, [connected, startDownloadTutorial, setStartDownloadTutorial])

  const previewVisible = usePreviewVisible()

  // ミッションごとのプレビュー初期表示形態（ミッション情報の取得前のみ非表示）。
  const previewDisplayMode = getPreviewDisplayMode(missionInfo)

  return (
    <PreviewControlProvider>
      <HeaderMenu
        type="editor"
        onTriggerDownloadTutorial={() => setStartDownloadTutorial(true)}
        onDownloadButtonClick={() => {
          if (startDownloadTutorial) {
            setStartDownloadTutorial(false)
          }
        }}
        onDownloadButtonFailed={() => setStartDownloadTutorial(false)}
      />
      <div className="App">
        <header className="App-header">
          <BlocklyComponent />
        </header>
      </div>
      <div id="code-editor-div" />
      {!isCopy && <WindowVideo startVideo={autoPlay} />}
      {/* プレビュー非表示時（siteName 未取得、または表示形態が hidden）は
          モーダルをマウントしない。usePreviewModal（scene fetch / workspace 購読）を
          含む PreviewModalHost ごと外し、非表示時は副作用も止める。ミッション情報
          取得前の表示形態（hidden）もここで適用する。 */}
      {previewVisible && previewDisplayMode !== 'hidden' && (
        <PreviewModalHost key={missionInfo?.id} missionInfo={missionInfo} />
      )}

      {isOnlineUser && <Tutorial tutorialEndCallback={handleTutorialEnd} />}
      <DownloadTutorial
        run={startDownloadTutorial && !connected}
        onComplete={() => setStartDownloadTutorial(false)}
      />
      <DevTools />
    </PreviewControlProvider>
  )
}

function Editor() {
  return (
    <ErrorBoundary linkTo="リスト">
      <ProjectProvider>
        <LinkageProvider>
          <EditorContent />
        </LinkageProvider>
      </ProjectProvider>
    </ErrorBoundary>
  )
}

export default Editor
